// Menu visibility presets are kept separate from the shell so they can be
// verified without rendering the full navigation tree.
(function registerMenuPreferences(root) {
  const AI_MODE_PAGE_KEYS = Object.freeze([
    'hjem',
    'superplumma',
    'aiautopilot',
    'aiinnsikt',
    'fakturaliste',
    'kunder',
    'produkter',
    'leverandorer',
  ]);
  const SIMPLE_SOLE_TRADER_PAGE_KEYS = Object.freeze([
    'superplumma',
    'aiautopilot',
    'aiinnsikt',
    'fakturaliste',
    'kunder',
    'produkter',
    'leverandorer',
    'innboks',
    'resultatregnskap',
    'saldobalanse',
    'skattemelding',
    'arsavslutning',
    'allebilag',
  ]);

  function simpleSoleTraderPageKeys({ vatRegistered = false, hasBusinessBankAccount = false } = {}) {
    return [
      ...SIMPLE_SOLE_TRADER_PAGE_KEYS,
      ...(vatRegistered === true ? ['innsendinger'] : []),
      ...(hasBusinessBankAccount === true ? ['bank'] : []),
    ];
  }

  function hiddenPagesForMode(navGroups, lockedPages, rulePages, visiblePageKeys) {
    const visible = new Set(visiblePageKeys || []);
    const next = {};
    (navGroups || []).forEach((group) => (group.items || []).forEach((item) => {
      if (!lockedPages[item.key] && !rulePages[item.key] && !visible.has(item.key)) next[item.key] = true;
    }));
    return next;
  }

  function matchesMode(navGroups, hiddenPages, lockedPages, rulePages, visiblePageKeys) {
    const visible = new Set(visiblePageKeys || []);
    return (navGroups || []).every((group) => (group.items || []).every((item) => {
      if (lockedPages[item.key] || rulePages[item.key]) return true;
      return Boolean(hiddenPages[item.key]) === !visible.has(item.key);
    }));
  }

  function hiddenPagesForSimpleSoleTrader(navGroups, lockedPages, rulePages, answers = {}) {
    return hiddenPagesForMode(navGroups, lockedPages, rulePages, simpleSoleTraderPageKeys(answers));
  }

  function filterNavigation(groups, hiddenPages, lockedPages, rulePages, unavailableKeys = []) {
    const unavailable = new Set(unavailableKeys);
    const visible = (item) => !unavailable.has(item.key) && (!hiddenPages[item.key] || lockedPages[item.key] || rulePages[item.key]);
    return (groups || []).flatMap((group) => {
      if (group.route) return visible({ key: group.route }) ? [group] : [];
      if (group.groups) {
        const nested = filterNavigation(group.groups, hiddenPages, lockedPages, rulePages, unavailableKeys);
        return nested.length ? [{ ...group, groups: nested }] : [];
      }
      const items = (group.items || []).filter(visible);
      return items.length ? [{ ...group, items }] : [];
    });
  }

  const BOTTOM_NAV = [
    { key: 'hjem', label: 'Hjem', labelKey: 'nav.home', icon: 'home', route: 'hjem' },
    { key: 'salg', label: 'Salg', labelKey: 'navgroup.sales', icon: 'fileText', items: [
      { key: 'faktura', label: 'Nytt salg', icon: 'fileText' }, { key: 'fakturaliste', label: 'Fakturaliste', icon: 'list' },
      { key: 'kunder', label: 'Kunder', icon: 'users' },
      { key: 'produkter', label: 'Produkter', icon: 'box' }, { key: 'prosjekter', label: 'Prosjekter', icon: 'clock' },
      { key: 'abonnement', label: 'Avtaler', icon: 'refresh' },
    ] },
    { key: 'kjop', label: 'Kjøp', labelKey: 'navgroup.purchases', icon: 'bag', items: [
      { key: 'nyttkjop', label: 'Nytt kjøp', icon: 'bag' }, { key: 'fripostering', label: 'Fri postering', icon: 'files' },
      { key: 'leverandorer', label: 'Leverandører', icon: 'building' },
      { key: 'eiendeler', label: 'Eiendeler', icon: 'briefcase' }, { key: 'investeringer', label: 'Investeringer', icon: 'trendUp' },
      { key: 'varelager', label: 'Varelager', icon: 'box' },
      { key: 'innboks', label: 'Innboks', icon: 'mail', color: 'var(--success)' },
    ] },
    { key: 'annet', label: 'Øvrig', labelKey: 'navgroup.miscellaneous', icon: 'dots', groups: [
      { label: 'Rapporter', labelKey: 'navgroup.reports', items: [
        { key: 'resultatregnskap', label: 'Resultatregnskap', icon: 'trendUp' },
        { key: 'saldobalanse', label: 'Saldobalanse', icon: 'sliders' },
      ] },
      { label: 'AI', labelKey: 'navgroup.ai', items: [
        { key: 'superplumma', label: 'SuperPlumma', icon: 'sparkle' },
    { key: 'aiautopilot', label: 'AI Regnskapsfører', icon: 'sparkle' },
        { key: 'aiinnsikt', label: 'AI Rådgiver', icon: 'trendUp' },
      ] },
      { label: 'Innleveringer', labelKey: 'navgroup.filings', items: [
        { key: 'innsendinger', label: 'MVA-melding', icon: 'percent' },
        { key: 'skattemelding', label: 'Skattemelding', icon: 'doc' },
        { key: 'arsavslutning', label: 'Årsavslutning', icon: 'checkCircle' },
        { key: 'arsregnskap', label: 'Årsregnskap', icon: 'files' },
      ] },
      { label: 'Ansatte', labelKey: 'navgroup.employees', items: [
        { key: 'lonn', label: 'Lønn', icon: 'wallet' },
        { key: 'kjorebok', label: 'Kjørebok', icon: 'car' },
      ] },
      { label: 'Annet', labelKey: 'navgroup.other', items: [
        { key: 'allebilag', label: 'Transaksjoner', icon: 'receipt' },
        { key: 'bank', label: 'Bank', icon: 'bank', color: '#2A6FDB' },
      ] },
      { label: 'Innstillinger', labelKey: 'navgroup.settings', items: [
        { key: 'support', label: 'Support', icon: 'chat' }, { key: 'innstillinger', label: 'Innstillinger', icon: 'settings' },
        { key: 'minside', label: 'Min side', icon: 'users' },
      ] },
    ] },
  ];

  root.PlummeMenuPreferences = Object.freeze({
    BOTTOM_NAV,
    filterNavigation,
    AI_MODE_PAGE_KEYS,
    SIMPLE_SOLE_TRADER_PAGE_KEYS,
    simpleSoleTraderPageKeys,
    hiddenPagesForMode,
    hiddenPagesForSimpleSoleTrader,
    matchesMode,
  });
})(typeof window !== 'undefined' ? window : globalThis);
